"use client";

/**
 * Deep Link Handler Component
 * Intercepts appUrlOpen events from Capacitor and routes them correctly
 * This ensures users return to the native app after Stripe checkout
 */

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { useAuthStore } from "@/stores/auth-store";
import { isNativeAppClient, normalizeInternalReturnPath } from "@/lib/utils/deep-links";
import { refreshMembershipState } from "@/lib/membership/refresh-membership";
import { Browser } from "@capacitor/browser";
import { useToast } from "@/hooks/use-toast";
import { apiUrl } from "@/lib/api-base";
import { loadPlugin } from "@/lib/capacitor/load-plugin";

export function DeepLinkHandler() {
  const router = useRouter();
  const { syncSession, updateMembershipTier } = useAuthStore();
  const toast = useToast();
  const lastRefreshAtRef = useRef(0);

  useEffect(() => {
    // Only run in native app
    if (!isNativeAppClient()) {
      return;
    }

    let urlListener: any = null;
    let resumeListener: any = null;
    let checkoutInProgress = false;
    const REFRESH_COOLDOWN_MS = 2000;

    const refreshMembershipIfNeeded = async () => {
      const now = Date.now();
      if (now - lastRefreshAtRef.current < REFRESH_COOLDOWN_MS) {
        return;
      }
      lastRefreshAtRef.current = now;
      await syncSession();
      await refreshMembershipState();
    };

    const setupDeepLinkListener = async () => {
      try {
        const mod = await loadPlugin("App", () => import("@capacitor/app"));
        if (!mod?.App) return; // older build with no App plugin: no deep links
        const { App } = mod;
        
        // Listen for deep link URLs (e.g., growthsync://app/checkout/success?session_id=xxx)
        urlListener = await App.addListener("appUrlOpen", async (data: { url: string }) => {
          try {
            // Close the in-app browser if still open
            try {
              await Browser.close();
            } catch {
              // Browser might not be open, ignore
            }
            
            // Parse the deep link URL
            // Format: growthsync://app/path?params
            // Note: URL constructor doesn't work with custom schemes, so we parse manually
            const urlStr = data.url;
            
            // Extract path and query string
            // growthsync://app/checkout/success?session_id=xxx
            const match = urlStr.match(/growthsync:\/\/app(\/[^?]*)(\?.*)?/);
            if (!match) {
              router.push("/settings");
              return;
            }
            
            const path = match[1] || "/";
            const queryString = match[2] || "";
            const params = new URLSearchParams(queryString);
            
            // Handle checkout success routes
            if (path.startsWith("/checkout/success")) {
              checkoutInProgress = false; // Clear flag
              
              const sessionId = params.get("session_id");
              
              // Refresh auth and profile state before navigating
              try {
                await syncSession();
                
                const authUser = useAuthStore.getState().user;
                if (authUser?.id && sessionId) {
                  // CRITICAL: Verify checkout session first to update DB immediately
                  // (fixes race where webhook hasn't run yet - user shows Premium instead of Elite)
                  try {
                    const verifyResponse = await fetch(apiUrl("/api/checkout/verify-session"), {
                      method: "POST",
                      credentials: "include",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ session_id: sessionId }),
                    });
                    if (verifyResponse.ok) {
                      const verifyData = await verifyResponse.json();
                      if (verifyData?.plan_tier && verifyData.plan_tier !== "free") {
                        updateMembershipTier(verifyData.plan_tier as "free" | "premium" | "elite");
                      }
                    }
                  } catch {
                    // Non-fatal - membership will refresh via webhook
                  }
                  
                  await refreshMembershipState();
                }
              } catch (refreshError) {
                console.error("[DeepLinkHandler] Error refreshing session/profile:", refreshError);
              }
              
              // Navigate to checkout success page with params
              const eventId = params.get("eventId");
              const type = params.get("type");
              
              let successPath = "/checkout/success";
              if (sessionId) {
                successPath += `?session_id=${encodeURIComponent(sessionId)}`;
                if (eventId) {
                  successPath += `&eventId=${encodeURIComponent(eventId)}`;
                }
                if (type) {
                  successPath += `&type=${encodeURIComponent(type)}`;
                }
              }
              
              router.push(successPath);
              return;
            }
            
            // Handle cancel routes - return to membership/settings screen
            if (path.startsWith("/checkout/cancel")) {
              checkoutInProgress = false; // Clear flag
              
              // Close browser if still open
              try {
                await Browser.close();
              } catch (browserError) {
                // Ignore
              }
              
              // Navigate back to the page that initiated checkout (or pricing by default).
              const returnPath = normalizeInternalReturnPath(params.get("return"), "/upgrade");
              router.push(returnPath);
              return;
            }
            
            // Handle old pricing cancel routes (backward compatibility)
            if (path.startsWith("/upgrade")) {
              const canceled = params.get("canceled");
              let pricingPath = "/upgrade";
              if (canceled) {
                pricingPath += `?canceled=${encodeURIComponent(canceled)}`;
              }
              router.push(pricingPath);
              return;
            }
            
            // Handle event routes
            if (path.startsWith("/events/")) {
              router.push(path);
              return;
            }
            
            // Handle Stripe billing portal return - refresh membership in case user updated payment or plan
            if (params.get("stripe_portal_return") === "1" && path && path !== "/") {
              try {
                await refreshMembershipIfNeeded();
              } catch (refreshError) {
                console.error("[DeepLinkHandler] Error refreshing membership after portal return:", refreshError);
              }
              router.push(path);
              return;
            }

            // Handle other routes
            if (path && path !== "/") {
              router.push(path);
              return;
            }
            
            // Default: go to settings (membership screen)
            router.push("/settings");
          } catch (error) {
            console.error("[DeepLinkHandler] Error handling deep link:", error);
            // Fallback: navigate to settings
            router.push("/settings");
          }
        });
        
        // Listen for app resume - refresh membership if checkout was in progress
        resumeListener = await App.addListener("appStateChange", async (state: { isActive: boolean }) => {
          if (state.isActive && checkoutInProgress) {
            checkoutInProgress = false;
            try {
              await refreshMembershipIfNeeded();
            } catch (error) {
              console.error("[DeepLinkHandler] Error refreshing on resume:", error);
            }
          }
        });
      } catch {
        // @capacitor/app not available (web build or not installed) - expected
      }
    };

    setupDeepLinkListener();

    return () => {
      if (urlListener) {
        urlListener.remove();
      }
      if (resumeListener) {
        resumeListener.remove();
      }
    };
  }, [router, syncSession, updateMembershipTier, toast]);

  // This component doesn't render anything
  return null;
}
